import Typography from 'components/Typography';
import Card from 'components/Card';
import CardContent from 'components/CardContent';

import {Prescription, RequestDetails, ScriptStatus} from 'types';

import styles from '../../RequestDetails.module.scss';
import {useCallback, useMemo, useState} from 'react';
import ProductItem from '../ProductItem';
import useSendCustomerNotification from 'hooks/useSendCustomerNotification';
import ButtonWithIcon from 'components/ButtonWithIcon';
import {
  EditIcon,
  PackageIcon,
} from 'pages/DeliveryPlanner/components/OrderDetailsModal/icons';
import PaymentDetail from '../PaymentDetail';
import EditModal from '../EditModal';
import NotifyModal from '../NotifyModal';
import {WrenchIcon} from 'pages/Signup/icons';
import ServiceItem from '../ServiceItem';
import Button from 'components/Button';
import PayableDetail from '../PayableDetail';
import FeatureGuard from 'components/FeatureGuard';
import {useCompanyConfig} from 'hooks/useCompanyConfig';
import OrganizerBySequence from 'components/OrganizerBySequence';

interface Props {
  data: RequestDetails;
  refetch: () => void;
  onSendNotification: (types: any, items: any[]) => Promise<any>;
  onCharge: (id: number | undefined) => Promise<any>;
  onChargeManually: (transactionType: 'payable' | 'receivable') => void;
  selectedSplitItems: number[];
  setSelectedSplitItems: (items: number[]) => void;
  isSplitting: boolean;
  onSplit: () => void;
}

function Prescriptions({
  data,
  refetch,
  onCharge,
  onSendNotification,
  onChargeManually,
  selectedSplitItems,
  setSelectedSplitItems,
  isSplitting,
  onSplit,
}: Props) {
  const {
    id: requestId = 0 as number,
    prescriptions: drugs = [],
    status = '',
  } = data || ({} as RequestDetails);

  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editType, setEditType] = useState<'service' | 'product' | 'none'>(
    'none'
  );
  const [notifyToPay, setNotifyToPay] = useState({
    is_sms: true,
    is_email: true,
    is_app_push: true,
  });
  const [paymentUrl, setPaymentUrl] = useState('');
  const {sendNotification} = useSendCustomerNotification();
  const {isDeliveryManager, isPickupDropoff, isPropertyManager, layoutConfig} =
    useCompanyConfig();

  const orderedServices = useMemo(() => {
    if (isDeliveryManager) return [];
    return drugs?.filter((item) => item.offering_type === 'service');
  }, [drugs, isDeliveryManager]);
  const orderedProducts = useMemo(() => {
    return drugs?.filter((item) => item.offering_type === 'product');
  }, [drugs]);

  const handleSendNotification = useCallback(
    (items: any[]) => {
      onSendNotification(notifyToPay, items);
      setIsNotifyModalOpen(false);
    },
    [onSendNotification, notifyToPay]
  );

  const handleOpenPaymentModal = useCallback(async () => {
    setIsNotifyModalOpen(true);
    const res = await sendNotification({
      requestId: data?.id,
      types: {
        is_sms: false,
        is_email: false,
        is_app_push: false,
      },
      request_drugs:
        data.status === 'completed'
          ? data.prescriptions.filter((item) => item.status !== 'completed')
          : data.prescriptions,
    });

    const paymentUrl = res.data.payment_link;
    setPaymentUrl(paymentUrl);
  }, [data, sendNotification]);

  const notifyToPayActions = useMemo(() => {
    const types = {
      is_sms: 'SMS',
      is_email: 'Email',
      is_app_push: 'App Push',
    };
    return Object.keys(notifyToPay).map((key) => {
      return (
        <div className={styles.notify_to_pay_action} key={key}>
          <input
            type="checkbox"
            className={styles.notify_to_pay_action__checkbox}
            checked={notifyToPay[key as keyof typeof notifyToPay]}
            onChange={() =>
              setNotifyToPay({
                ...notifyToPay,
                [key]: !notifyToPay[key as keyof typeof notifyToPay],
              })
            }
          />
          <Typography variant="h3">
            {types[key as keyof typeof types]}
          </Typography>
        </div>
      );
    });
  }, [notifyToPay]);

  const openEditModal = useCallback((type: 'service' | 'product') => {
    setEditType(type);
    setShowEditModal(true);
  }, []);

  const disableToCheck = selectedSplitItems.length === drugs?.length - 1;
  const orderType = data?.order_type;
  const showSplitCheckbox = useMemo(() => {
    const status = drugs?.[0]?.status;
    return (
      status === ('in_progress' as ScriptStatus) ||
      status === ('new' as ScriptStatus)
    );
  }, [drugs]);
  return (
    <>
      <OrganizerBySequence sequences={layoutConfig.prescriptionsSequence}>
        <FeatureGuard key="1" feature="hasMoneyInOnOrderDetails">
          {((isPickupDropoff && orderType === 'pickup') ||
            isPropertyManager) && (
            <PayableDetail
              drugs={drugs as Prescription[]}
              status={status}
              onChargeManually={() => onChargeManually('payable')}
            />
          )}
        </FeatureGuard>
        <PaymentDetail
          key="2"
          drugs={drugs as Prescription[]}
          status={status}
          onCharge={onCharge}
          onNotifyToPay={handleOpenPaymentModal}
          onChargeManually={() => onChargeManually('receivable')}
          orderType={orderType}
        />
        {selectedSplitItems?.length > 0 && (
          <Card key="3" className={styles.card}>
            <CardContent pt="sm" pb="sm">
              <div className={styles.infoRow}>
                <Typography variant="h3">
                  Split the selected items into a new order.
                </Typography>
                <Button onClick={onSplit} disabled={isSplitting}>
                  {isSplitting ? 'Splitting...' : 'Split'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        <FeatureGuard feature="hasServicesOnOrderDetails" key="4">
          <Card className={styles.card}>
            <CardContent pt="sm" pb="sm">
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>
                  <WrenchIcon width={24} height={24} color="#1A6AE5" />
                  Services
                </h2>
                <ButtonWithIcon
                  icon={<EditIcon width={16} height={16} />}
                  color="ghost"
                  size="md"
                  onClick={() => openEditModal('service')}
                />
              </div>
              {orderedServices?.map((service) => (
                <ServiceItem
                  showSplitCheckbox={showSplitCheckbox}
                  disabledToCheck={disableToCheck}
                  selectedSplitItems={selectedSplitItems}
                  setSelectedSplitItems={setSelectedSplitItems}
                  refetch={refetch}
                  key={service.id}
                  product={service}
                  orderType={orderType}
                />
              ))}
              {orderedServices?.length === 0 && (
                <p className={styles.no_products}>No services</p>
              )}
            </CardContent>
          </Card>
        </FeatureGuard>
        <Card className={styles.card} key="5">
          <CardContent pt="sm" pb="sm">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <PackageIcon width={24} height={24} color="#1A6AE5" />
                Products
              </h2>
              <ButtonWithIcon
                icon={<EditIcon width={16} height={16} />}
                color="ghost"
                size="md"
                onClick={() => openEditModal('product')}
              />
            </div>
            {orderedProducts?.map((product) => (
              <ProductItem
                showSplitCheckbox={showSplitCheckbox}
                disabledToCheck={disableToCheck}
                selectedSplitItems={selectedSplitItems}
                setSelectedSplitItems={setSelectedSplitItems}
                refetch={refetch}
                key={product.id}
                product={product}
                orderType={orderType}
              />
            ))}
            {orderedProducts.length === 0 && (
              <p className={styles.no_products}>No products</p>
            )}
          </CardContent>
        </Card>
      </OrganizerBySequence>

      {showEditModal && (
        <EditModal
          requestId={requestId as number}
          refetch={refetch}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditType('none');
          }}
          product={editType === 'product' ? orderedProducts : orderedServices}
          editType={editType}
        />
      )}

      {isNotifyModalOpen && (
        <NotifyModal
          onClose={() => setIsNotifyModalOpen(false)}
          isOpen={isNotifyModalOpen}
          notifyToPayActions={notifyToPayActions}
          data={data}
          paymentUrl={paymentUrl}
          handleSendNotification={handleSendNotification}
        />
      )}
    </>
  );
}

export default Prescriptions;
